import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { couponService } from '../services/dashboardService';
import { productService } from '../services/productService';
import { fieldClass, labelClass, parseNumber } from '../utils/form';
import { NumberField } from '../components/NumberField';
import { rowNumber } from '../utils/rowNumber';
import type { Category } from '../types';

type DiscountType = 'percentage' | 'fixed';

type CouponRow = {
  id: string;
  code: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  minOrderAmount: number;
  maxDiscount: number | null;
  usageLimit: number | null;
  usagePerCustomer: number | null;
  usedCount: number;
  startsAt?: string;
  expiresAt?: string;
  categoryIds: string[];
  customerPhones: string;
  isActive: boolean;
};

type CouponFormState = {
  code: string;
  description: string;
  discountType: DiscountType;
  discountValue: number;
  minOrderAmount: number;
  maxDiscount: string;
  usageLimit: string;
  usagePerCustomer: string;
  startsAt: string;
  expiresAt: string;
  categoryIds: string[];
  customerPhones: string;
  isActive: boolean;
};

const emptyForm = (): CouponFormState => ({
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 0,
  minOrderAmount: 0,
  maxDiscount: '',
  usageLimit: '',
  usagePerCustomer: '1',
  startsAt: '',
  expiresAt: '',
  categoryIds: [],
  customerPhones: '',
  isActive: true,
});

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  const n = parseNumber(String(value ?? ''));
  return Number.isFinite(n) ? n : fallback;
}

function toDateInput(value: unknown): string {
  if (!value) return '';
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function mapCoupon(raw: Record<string, unknown>): CouponRow {
  const typeRaw = String(raw.discountType ?? raw.type ?? 'percentage').toLowerCase();
  const discountType: DiscountType =
    typeRaw === 'fixed' || typeRaw === 'amount' ? 'fixed' : 'percentage';
  const expires = raw.expiresAt ?? raw.expiryDate ?? raw.endDate;
  const starts = raw.startsAt ?? raw.startDate;
  const maxDiscount =
    raw.maxDiscount === null || raw.maxDiscount === undefined ? null : num(raw.maxDiscount);
  const usageLimit =
    raw.usageLimit === null || raw.usageLimit === undefined ? null : num(raw.usageLimit);
  const usagePerCustomer =
    raw.usagePerCustomer === null || raw.usagePerCustomer === undefined
      ? null
      : num(raw.usagePerCustomer);
  const catRaw = (raw.categoryIds as unknown[]) ?? [];
  const phonesRaw = raw.customerPhones;
  let phones = '';
  if (Array.isArray(phonesRaw)) {
    phones = phonesRaw.map((p) => String(p)).filter(Boolean).join('\n');
  } else if (typeof phonesRaw === 'string') {
    phones = phonesRaw;
  }

  return {
    id: String(raw.id ?? raw.code ?? ''),
    code: String(raw.code ?? ''),
    description: String(raw.description ?? ''),
    discountType,
    discountValue: num(raw.discountValue ?? raw.value),
    minOrderAmount: num(raw.minOrderAmount),
    maxDiscount,
    usageLimit,
    usagePerCustomer,
    usedCount: num(raw.usedCount),
    startsAt: starts ? String(starts) : undefined,
    expiresAt: expires ? String(expires) : undefined,
    categoryIds: catRaw.map((c) => String(c)).filter(Boolean),
    customerPhones: phones,
    isActive: Boolean(raw.isActive),
  };
}

function formFromRow(row: CouponRow): CouponFormState {
  return {
    code: row.code,
    description: row.description,
    discountType: row.discountType,
    discountValue: row.discountValue,
    minOrderAmount: row.minOrderAmount,
    maxDiscount: row.maxDiscount === null ? '' : String(row.maxDiscount),
    usageLimit: row.usageLimit === null ? '' : String(row.usageLimit),
    usagePerCustomer: row.usagePerCustomer === null ? '' : String(row.usagePerCustomer),
    startsAt: toDateInput(row.startsAt),
    expiresAt: toDateInput(row.expiresAt),
    categoryIds: row.categoryIds,
    customerPhones: row.customerPhones,
    isActive: row.isActive,
  };
}

function payloadFromForm(form: CouponFormState): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    code: form.code.trim(),
    description: form.description.trim(),
    discountType: form.discountType,
    discountValue: num(form.discountValue),
    minOrderAmount: num(form.minOrderAmount),
    isActive: form.isActive,
    categoryIds: form.categoryIds.map((id) => Number(id)).filter((n) => n > 0),
    customerPhones: form.customerPhones
      .split(/[\n,;]+/)
      .map((p) => p.trim())
      .filter(Boolean),
  };

  const maxTrim = form.maxDiscount.trim();
  payload.maxDiscount = maxTrim === '' ? null : num(maxTrim);

  const usageTrim = form.usageLimit.trim();
  payload.usageLimit = usageTrim === '' ? null : num(usageTrim);

  // Always one redemption per customer (enforced by API as well).
  payload.usagePerCustomer = 1;

  payload.startsAt = form.startsAt.trim() || null;
  payload.expiresAt = form.expiresAt.trim() || null;

  return payload;
}

export function DiscountsPage() {
  const [codes, setCodes] = useState<CouponRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CouponRow | null>(null);
  const [form, setForm] = useState<CouponFormState>(emptyForm);

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const [data, cats] = await Promise.all([
        couponService.list(),
        productService.getCategories().catch(() => [] as Category[]),
      ]);
      setCodes((data as unknown as Record<string, unknown>[]).map(mapCoupon));
      setCategories(cats ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت تخفیف‌ها');
      setCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCoupons();
  }, [loadCoupons]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row: CouponRow) => {
    setEditing(row);
    setForm(formFromRow(row));
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!form.code.trim()) {
      toast.error('کد تخفیف الزامی است');
      return;
    }
    if (form.discountValue <= 0) {
      toast.error('مقدار تخفیف باید بیشتر از صفر باشد');
      return;
    }
    if (form.discountType === 'percentage' && form.discountValue > 100) {
      toast.error('درصد تخفیف نمی‌تواند بیشتر از ۱۰۰ باشد');
      return;
    }
    if (form.startsAt && form.expiresAt && form.startsAt > form.expiresAt) {
      toast.error('تاریخ شروع نمی‌تواند بعد از انقضا باشد');
      return;
    }

    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing) {
        await couponService.update(editing.id, payload);
        toast.success('کد تخفیف ویرایش شد');
      } else {
        await couponService.create(payload);
        toast.success('کد تخفیف ایجاد شد');
      }
      setFormOpen(false);
      setEditing(null);
      setForm(emptyForm());
      await loadCoupons();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره کد تخفیف ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: CouponRow) => {
    if (!confirm(`آیا از حذف کد «${row.code}» مطمئن هستید؟`)) return;
    try {
      await couponService.remove(row.id);
      toast.success('کد تخفیف حذف شد');
      await loadCoupons();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف کد تخفیف ناموفق بود');
    }
  };

  const setField = <K extends keyof CouponFormState>(key: K, value: CouponFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCategory = (id: string) => {
    setForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(id)
        ? prev.categoryIds.filter((c) => c !== id)
        : [...prev.categoryIds, id],
    }));
  };

  const categoryLabel = (ids: string[]) => {
    if (!ids.length) return 'همه دسته‌ها';
    const names = ids
      .map((id) => categories.find((c) => String(c.id) === id)?.name)
      .filter(Boolean);
    return names.length ? names.join('، ') : `${ids.length} دسته`;
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">کدهای تخفیف</h1>
          <p className="text-slate-600 mt-1">مدیریت کدهای تخفیف و کوپن‌ها</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن کد تخفیف
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">ردیف</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">کد</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">
                  نوع تخفیف
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">
                  میزان تخفیف
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">محدودیت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">
                  استفاده‌های موفق
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">مدت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">وضعیت</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-8 text-center text-slate-500">
                    کد تخفیفی یافت نشد
                  </td>
                </tr>
              ) : (
                codes.map((code, index) => {
                  const isPercentage = code.discountType === 'percentage';
                  const periodBits = [
                    code.startsAt
                      ? `از ${new Date(code.startsAt).toLocaleDateString('fa-IR')}`
                      : null,
                    code.expiresAt
                      ? `تا ${new Date(code.expiresAt).toLocaleDateString('fa-IR')}`
                      : null,
                  ].filter(Boolean);
                  return (
                    <tr
                      key={code.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm tabular-nums text-slate-500">
                        {rowNumber(index)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-indigo-600">
                        {code.code}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {isPercentage ? 'درصدی' : 'مبلغ ثابت'}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {code.discountValue}
                        {isPercentage ? '%' : ' تومان'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        <div>{categoryLabel(code.categoryIds)}</div>
                        {code.customerPhones.trim() ? (
                          <div className="text-xs text-amber-700 mt-0.5">مشتریان خاص</div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {code.usedCount}
                        {code.usageLimit !== null ? ` / ${code.usageLimit}` : ''}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {periodBits.length ? periodBits.join(' ') : 'بدون محدودیت زمانی'}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${
                            code.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {code.isActive ? 'فعال' : 'غیرفعال'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(code)}
                            className="text-blue-600 hover:text-blue-700 p-1"
                            title="ویرایش"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(code)}
                            className="text-red-600 hover:text-red-700 p-1"
                            title="حذف"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش کد تخفیف' : 'افزودن کد تخفیف'}
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div>
            <label className={labelClass}>کد</label>
            <input
              className={fieldClass}
              value={form.code}
              onChange={(e) => setField('code', e.target.value.toUpperCase())}
              dir="ltr"
              placeholder="EVA10"
            />
          </div>
          <div>
            <label className={labelClass}>توضیحات</label>
            <textarea
              className={fieldClass}
              rows={2}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              dir="rtl"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>نوع تخفیف</label>
              <select
                className={fieldClass}
                value={form.discountType}
                onChange={(e) => setField('discountType', e.target.value as DiscountType)}
              >
                <option value="percentage">درصدی</option>
                <option value="fixed">مبلغ ثابت</option>
              </select>
            </div>
            <NumberField
              label={`مقدار تخفیف ${form.discountType === 'percentage' ? '(٪)' : '(تومان)'}`}
              value={String(form.discountValue || 0)}
              onChange={(v) => setField('discountValue', num(v))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberField
              label="حداقل مبلغ سفارش (تومان)"
              value={String(form.minOrderAmount || 0)}
              onChange={(v) => setField('minOrderAmount', num(v))}
            />
            <NumberField
              label="سقف تخفیف (تومان، اختیاری)"
              value={form.maxDiscount}
              onChange={(v) => setField('maxDiscount', v)}
              placeholder="خالی = بدون سقف"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberField
              label="سقف تعداد استفاده کل (اختیاری)"
              value={form.usageLimit}
              onChange={(v) => setField('usageLimit', v)}
              placeholder="خالی = نامحدود"
              grouped={false}
            />
            <div>
              <label className={labelClass}>سقف استفاده هر مشتری</label>
              <input className={fieldClass} value="۱ بار (ثابت)" disabled dir="rtl" />
              <p className="mt-1 text-xs text-slate-500">هر شماره موبایل فقط یک‌بار می‌تواند از هر کد استفاده کند.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>تاریخ شروع (اختیاری)</label>
              <input
                type="date"
                className={fieldClass}
                value={form.startsAt}
                onChange={(e) => setField('startsAt', e.target.value)}
                dir="ltr"
              />
            </div>
            <div>
              <label className={labelClass}>تاریخ انقضا (اختیاری)</label>
              <input
                type="date"
                className={fieldClass}
                value={form.expiresAt}
                onChange={(e) => setField('expiresAt', e.target.value)}
                dir="ltr"
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>دسته‌بندی‌های مجاز (خالی = همه)</label>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
              {categories.length === 0 ? (
                <p className="text-sm text-slate-500 px-1 py-2">دسته‌ای یافت نشد</p>
              ) : (
                categories.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer px-1 py-1 rounded hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={form.categoryIds.includes(String(c.id))}
                      onChange={() => toggleCategory(String(c.id))}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    {c.name}
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <label className={labelClass}>شماره موبایل مشتریان خاص (اختیاری)</label>
            <textarea
              className={fieldClass}
              rows={3}
              value={form.customerPhones}
              onChange={(e) => setField('customerPhones', e.target.value)}
              dir="ltr"
              placeholder="هر شماره در یک خط یا با ویرگول — خالی = همه مشتریان"
            />
            <p className="mt-1 text-xs text-slate-500">
              اگر پر شود، فقط همین شماره‌ها می‌توانند از کد استفاده کنند.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setField('isActive', e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            فعال
          </label>
        </div>
      </Modal>
    </div>
  );
}
